import React, { useEffect, useMemo, useState } from 'react';
import { PanelBarGLPure, GridSelectionHLPure, ButtonAddHLPure, InteractiveDialog, InputHLPure, DropDownBtnHLPure, LabelErrorPure, LabelHLPure, InputSwitchHLPure, DataPickerHLPure, CheckBoxHLPure, DialogSystemGLPure } from '../../pures';
import { Grid, useMediaQuery } from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'
import { InteractiveContainer, InteractiveItem } from '../../layouts'
import { dataTypeOptions } from '../administration-types-of-documents/administration-type-documents-data-profile.view'
import { FormatTextUtil } from '../../../utils';
import './document-panel-bar-variables.css'
import { ArrowBtnMobile } from '../../../assets/logos/icons';

/**
 * @param {{
 *  variables: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse[],
 *  variableSelected: import('../../../types/document-type.types').Variable,
 *  setVariableSelected: (variable: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse) => Promise<void>,
 *  formatVariables: import('../../../types/Questionnaire.types').FormatVariable[],
 *  createVariable: (variable: import('../../../types/DocumentVariable.types').DocumentVariablePOSTData) => Promise<boolean>,
 *  updateVariable: (variable: import('../../../types/DocumentVariable.types').DocumentVariablePOSTData) => Promise<boolean>,
 *  createOrUpdateVariable?: (variable: import('../../../types/DocumentVariable.types').DocumentVariablePOSTData) => Promise<boolean>,
 *  deleteVariable: (variable: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse | number) => Promise<boolean>,
 *  clearVariableValue?: (variable: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse | { id: number } | number) => Promise<boolean>,
 *  skipDeleteConfirmation?: boolean,
 *  prefilledCreation?: { value: string | Date, type?: string, entityId?: string | number } | null,
 *  onPrefillConsumed?: () => void,
 *  documentData?: { id: number, typeDocumentId: number },
 *  entities?: Array<{ type: string, name: string, id?: string | number }>,
 *  entitiesStatus?: string,
 *  isInDeleteFlow?: boolean,
 * }} props
 * @return {*}
 */
const DocumentPanelBarVariables = (props) => {
    let {
        variables,
        formatVariables,
        createVariable,
        updateVariable,
        createOrUpdateVariable,
        prefilledCreation,
        onPrefillConsumed,
        entities = [],
        entitiesStatus,
        isInDeleteFlow,
        skipDeleteConfirmation = false,
        source
    } = props

    const formatTextUtil = new FormatTextUtil()
    const { t } = useTranslation()
    const [fieldErrors, setFieldErrors] = useState(/** @type {string[]} */([]))
    const [selectedVariable, setSelectedVariable] = useState(/** @type {import('../../../types/DocumentVariable.types').DocumentVariableGETResponse | null} */(null))
    const [documentVariable, setDocumentVariable] = useState(/** @type {import('../../../types/DocumentVariable.types').DocumentVariableCreateState} */({
        name: '',
        typeData: 'text',
        formatVariableKey: '',
        value: '',
        values: {
            true: '',
            false: '',
            checked: false
        },
        readingValue: ''
    }))
    const [dialogStates, setDialogStates] = useState({
        showEditDialog: false,
        showDeleteDialog: false,
        showCreationDialog: false
    })
    const [lockPrefilledValue, setLockPrefilledValue] = useState(false)
    const [gridOptions, setGridOptions] = useState(
        /** @type {{ nameComponentUser: string, showDetails: boolean, dataItem: import('../../../types/DocumentVariable.types').DocumentVariableEditState | null }} */({
            nameComponentUser: '',
            showDetails: false,
            dataItem: null
        })
    )

    // Entity-driven creation UI state
    const [useEntityValue, setUseEntityValue] = useState(false)
    const [selectedEntityType, setSelectedEntityType] = useState('')
    const [selectedEntityValue, setSelectedEntityValue] = useState('')
    const [isPrefillMode, setIsPrefillMode] = useState(false)

    // Edit modal: entity-driven UI state
    const [useEntityValueEdit, setUseEntityValueEdit] = useState(false)
    const [selectedEntityTypeEdit, setSelectedEntityTypeEdit] = useState('')
    const [selectedEntityValueEdit, setSelectedEntityValueEdit] = useState('')

    const is1040 = useMediaQuery('(max-width:1040px)')
    const is960 = useMediaQuery('(max-width:960px)');
    const is860 = useMediaQuery('(max-width:860px)');
    const is530 = useMediaQuery('(max-width:530px)');

    /**
     * Builds a numeric placeholder example formatted with the current formatVariableKey.
     * Falls back to "1000.00" when no format is provided.
     * @param {string} formatKey
     */
    const getNumericPlaceholder = (formatKey = '') => {
        let base
        switch (String(formatKey)) {
            case 'commaTwoDecimals':
                base = '1000.00'
                break
            case 'noCommaTwoDecimals':
                base = '1000.00'
                break
            case 'commaOneDecimal':
                base = '1000.0'
                break
            case 'noCommaOneDecimal':
                base = '1000.0'
                break
            case 'comma':
                base = '1000'
                break
            case 'noComma':
                base = '1000'
                break
            default:
                base = '1000.00'
                break
        }

        try {
            const formatted = formatTextUtil.formatVariableValue(base, formatKey || 'noCommaTwoDecimals')
            return formatted || base
        } catch {
            return base
        }
    }

    /** @param {import('../../../types/DocumentVariable.types').DocumentVariableEditState} documentVariable */
    const formatDocumentVariableValue = (documentVariable) => {
        if (documentVariable.typeData === 'date') {
            if (documentVariable.value instanceof Date) {
                return DateTime.fromJSDate(documentVariable.value).toFormat('dd-LL-yyyy');
            }
            if (typeof documentVariable.value === 'string') {
                const dt = DateTime.fromISO(documentVariable.value);
                return dt.isValid ? dt.toFormat('dd-LL-yyyy') : documentVariable.value;
            }
            return '';
        }
        if (documentVariable.typeData === 'date') {
            if (documentVariable.value instanceof Date) return DateTime.fromJSDate(documentVariable.value).toFormat('dd-LL-yyyy');
            if (typeof documentVariable.value === 'string') {
                const dt = DateTime.fromISO(documentVariable.value);
                return dt.isValid ? dt.toFormat('dd-LL-yyyy') : ''
            }
            return ''
        }
        return documentVariable.value ?? '';
    }

    const formatCopy = useMemo(() => {
        if (!documentVariable?.typeData) return formatVariables.filter(f => f.groupName === 'text')
        if (documentVariable.typeData === 'boolean') return formatVariables
        return formatVariables.filter(f => f.groupName === documentVariable.typeData)
    }, [documentVariable.typeData, formatVariables])

    /**
     * Genera las opciones para el dropdown de entidades según el tipo de dato.
     * @param {Array<{ type: string, name: string }>} entities
     * @param {string} typeData
     * @param {string} entityType
     * @param {boolean} isTypeDropdown Si es para el dropdown de tipo o de valor
     * @returns {Array<{ key: string, name: string }>}
     */
    function getEntityDropdownOptions(entities, typeData, entityType = '', isTypeDropdown = false) {
        const unassignedOption = { key: '', name: t('digitalVaultUploadView.unassigned') }
        let filteredEntities = entities || []

        switch (typeData) {
            case 'number':
                filteredEntities = filteredEntities.filter(e => e.type === 'Number' || e.type === 'Números')
                break
            case 'date':
                filteredEntities = filteredEntities.filter(e => e.type === 'Date' || e.type === 'Fechas')
                break
            case 'text':
                filteredEntities = filteredEntities.filter(e => e.type !== 'Number' && e.type !== 'Números' && e.type !== 'Date' && e.type !== 'Fechas')
                break
            default:
                break
        }

        if (entityType) {
            filteredEntities = filteredEntities.filter(e => e.type === entityType)
        }

        let options
        if (isTypeDropdown) {
            options = Array.from(new Set(filteredEntities.map(e => e.type))).map(type => ({ key: type, name: type }))
        } else {
            options = filteredEntities.map(ent => ({ key: ent.name, name: ent.name }))
        }
        options.unshift(unassignedOption)
        return options
    }

    // Ensure a formatVariableKey is selected when changing type (except boolean)
    useEffect(() => {
        if (!documentVariable.typeData || documentVariable.typeData === 'boolean') return
        if (!documentVariable.formatVariableKey) {
            const firstKey = formatVariables?.find(f => f.groupName === documentVariable.typeData)?.key
            if (firstKey) setDocumentVariable(prev => ({ ...prev, formatVariableKey: firstKey }))
        }
    }, [documentVariable.typeData, documentVariable.formatVariableKey, formatVariables])

    // Note: update flow not used in current UI; remove canBeUpdated to avoid unused var
    /**
     * @param {import('../../../types/DocumentVariable.types').DocumentVariableGETResponse} dataItem
     */
    const onClickDeleteDialog = (dataItem) => {
        setSelectedVariable(dataItem)
        setDialogStates(prev => ({
            ...prev,
            showDeleteDialog: true
        }))
    }

    /**
     * Delete without confirmation: clear value immediately
     * @param {import('../../../types/DocumentVariable.types').DocumentVariableGETResponse} dataItem
     */
    const onClickDeleteDirect = async (dataItem) => {
        const maybeClear = (props).clearVariableValue
        if (typeof maybeClear === 'function') {
            await maybeClear(dataItem)
        }
    }

    const onCloseDeleteDialog = () => {
        setSelectedVariable(null)
        setDialogStates(prev => ({
            ...prev,
            showDeleteDialog: false
        }))
    }

    const onCloseEditDialog = () => {
        setGridOptions(prev => ({
            ...prev,
            nameComponentUser: '',
            showDetails: false,
            dataItem: null
        }))
        setUseEntityValueEdit(false)
        setSelectedEntityTypeEdit('')
        setSelectedEntityValueEdit('')
        setFieldErrors([])
    }

    useEffect(() => {
        // Establecer un tipo por defecto al cargar el componente
        if (formatVariables && formatVariables.length > 0) {
            const defaultType = 'text'
            const defaultFormat = formatVariables.find(f => f.groupName === defaultType)?.key
            setDocumentVariable(prev => ({
                ...prev,
                typeData: defaultType,
                formatVariableKey: defaultFormat || ''
            }))
        }
    }, [formatVariables])

    useEffect(() => {
        if (documentVariable.typeData === 'boolean' && useEntityValue) {
            setUseEntityValue(false)
            setSelectedEntityType('')
            setSelectedEntityValue('')
        }
    }, [documentVariable.typeData, useEntityValue])

    /**
     * @param {any} entityType
     */
    const mapEntityTypeToDataType = (entityType) => {
        if (!entityType || typeof entityType !== 'string') return 'text'
        const normalized = entityType.toString().trim().toLowerCase()
        if (/(n[uú]mero|number|num)/.test(normalized)) return 'number'
        if (/(fecha|date)/.test(normalized)) return 'date'
        return 'text'
    }


    useEffect(() => {
        if (prefilledCreation && prefilledCreation.value) {
            const mappedType = mapEntityTypeToDataType(prefilledCreation.type)
            const firstFormatKey = mappedType === 'boolean'
                ? ''
                : (formatVariables?.find(f => f.groupName === mappedType)?.key || '')

            setDocumentVariable({
                name: '',
                typeData: mappedType,
                formatVariableKey: firstFormatKey,
                value: mappedType === 'date' ? null : prefilledCreation.value,
                values: { true: '', false: '', checked: false },
                readingValue: prefilledCreation.value
            })
            setLockPrefilledValue(true)
            setUseEntityValue(false)
            setIsPrefillMode(true)
            setDialogStates(prev => ({ ...prev, showCreationDialog: true }))
            if (onPrefillConsumed) onPrefillConsumed()
        }
    }, [prefilledCreation, formatVariables, onPrefillConsumed])

    /**
     * @param {string} field
     * @param {any} value
     */
    const setValue = (field, value) => {
        let newValue = value
        if (
            field === "value" &&
            documentVariable.typeData !== "date" &&
            documentVariable.typeData !== "boolean"
        ) {
            newValue = formatTextUtil.formatVariableValue(value, documentVariable.formatVariableKey)
        }
        if (field === "typeData" && documentVariable.typeData !== value) {
            setDocumentVariable((prev) => ({
                ...prev,
                formatVariableKey: '',
                // For date, keep null so the picker starts empty
                value: value === 'date' ? null : '',
                values: { true: '', false: '', checked: false },
            }))
        }
        setDocumentVariable((prev) => ({
            ...prev,
            [field]: newValue
        }))
    }

    const dataOptions = [...dataTypeOptions].map((item) => ({
        ...item,
        name: t(item.name)
    }))

    const validateData = () => {
        let invalidFields = []
        if (!documentVariable?.name || !documentVariable?.name?.trim()) invalidFields.push('name')
        if (!documentVariable?.typeData) invalidFields.push('typeData')
        if (documentVariable.readingValue && !documentVariable.value && documentVariable.typeData === 'date') invalidFields.push('entityDate')
        if (documentVariable?.typeData !== 'boolean' && !documentVariable?.formatVariableKey) invalidFields.push('formatVariableKey')
        if (documentVariable?.typeData === 'boolean') {
            if (!documentVariable?.values?.true || !documentVariable?.values?.true?.trim()) invalidFields.push('valueTrue')
            if (!documentVariable?.values?.false || !documentVariable?.values?.false?.trim()) invalidFields.push('valueFalse')
        }

        if (useEntityValue) {
            if (!selectedEntityType || selectedEntityType === '') invalidFields.push('entityType')
            if (!selectedEntityValue || selectedEntityValue === '') invalidFields.push('entityValue')
        }

        setFieldErrors(invalidFields)
        return { isValid: invalidFields.length === 0, invalidFields }
    }

    const toggleCreationDialog = () => {
        const defaultType = 'text'
        const defaultFormatKey = (formatVariables?.find(f => f.groupName === defaultType)?.key) || ''
        setDocumentVariable({
            name: '',
            typeData: defaultType,
            formatVariableKey: defaultFormatKey,
            value: '',
            values: {
                true: '',
                false: '',
                checked: false
            }
        })
        setLockPrefilledValue(false)
        setUseEntityValue(false)
        setSelectedEntityType('')
        setSelectedEntityValue('')
        setFieldErrors([])
        setIsPrefillMode(false)
        setDialogStates(prev => ({
            ...prev,
            showCreationDialog: !prev.showCreationDialog
        }))
    }

    const onCreate = async () => {
        const { isValid, invalidFields } = validateData()
        if (!isValid) {
            setFieldErrors(invalidFields)
            return
        }

        const isDate = documentVariable.typeData === 'date'
        const valueIso = isDate
            ? (documentVariable.value instanceof Date
                ? documentVariable.value.toISOString()
                : (typeof documentVariable.value === 'string' ? documentVariable.value : null))
            : null

        const valueDisplay = isDate && valueIso
            ? (DateTime.fromISO(valueIso).isValid
                ? DateTime.fromISO(valueIso).toFormat('dd-LL-yyyy')
                : valueIso)
            : (documentVariable.value ?? null)

        const typeDocumentId = Number(props.documentData?.typeDocumentId)
        const docIdRaw = props.documentData?.id
        const variableToCreate = {
            variableId: null,
            typeDocumentId: Number.isFinite(typeDocumentId) && typeDocumentId > 0 ? typeDocumentId : 0,
            documentId: docIdRaw ?? null,
            variableName: (documentVariable.name || '').trim(),
            typeData: documentVariable.typeData,
            formatVariableKey: documentVariable.formatVariableKey || '',
            dataProfile: null,
            mandatory: false,
            valueVariable: documentVariable.typeData === 'boolean'
                ? {
                    true: documentVariable.values.true,
                    false: documentVariable.values.false,
                    checked: documentVariable.values.checked
                }
                : documentVariable.value,
            valueIso,
            valueDisplay,
            entityType: useEntityValue ? selectedEntityType : (prefilledCreation?.type ?? null),
            entityId: useEntityValue ? entities.find(e => e.name === selectedEntityValue)?.id ?? null : null,
            source: isPrefillMode ? 'entity-convert' : 'variables-panel'
        }
        toggleCreationDialog()
        const fn = (typeof createOrUpdateVariable === 'function') ? createOrUpdateVariable : createVariable
        await fn(variableToCreate)
    }

    const onSaveEdit = async () => {
        const dataItem = gridOptions.dataItem
        if (!dataItem) return

        let invalidFields = []
        // Validación similar a la creación
        if (!dataItem?.name || !String(dataItem?.name).trim()) invalidFields.push('name')
        if (!dataItem?.typeData) invalidFields.push('typeData')
        if (dataItem?.typeData !== 'boolean' && !dataItem?.formatVariableKey) invalidFields.push('formatVariableKey')

        if (useEntityValue) {
            // Solo valida los campos de creación
            if (!selectedEntityTypeEdit || selectedEntityTypeEdit === '') invalidFields.push('entityType')
            if (!selectedEntityValueEdit || selectedEntityValueEdit === '') invalidFields.push('entityValue')
        }

        if (dataItem?.typeData === 'date') {
            // Para date, debe haber un valor válido
            const v = dataItem.value
            let isValidDate = false
            if (v instanceof Date && !isNaN(v.getTime())) isValidDate = true
            if (typeof v === 'string' && DateTime.fromISO(v).isValid) isValidDate = true
            if (!isValidDate) invalidFields.push('valueDate')
        } else {
            if (!dataItem?.value || !String(dataItem?.value).trim()) invalidFields.push('value')
        }

        setFieldErrors(invalidFields)
        if (invalidFields.length > 0) return // No guardar si hay errores

        let valueToSend
        switch (dataItem.typeData) {
            case 'boolean': {
                valueToSend = source === 'detailView' ? dataItem.values : dataItem.value
                break
            }
            case 'date': {
                const v = (dataItem.value)
                if (v instanceof Date) {
                    valueToSend = v.toISOString()
                } else {
                    valueToSend = dataItem.value
                        ? new Date((dataItem.value)).toISOString()
                        : null
                }
                break
            }
            case 'number': {
                valueToSend = dataItem.value
                break
            }
            default:
                valueToSend = dataItem.value
                break
        }

        const resolvedVarId = ((dataItem).variableId) || dataItem.id || (
            Array.isArray(variables)
                ? (variables.find(v => ((v).name === dataItem.name || (v).variableName === dataItem.name))?.id)
                : undefined
        )

        const variableToUpdate = {
            id: resolvedVarId,
            variableId: resolvedVarId,
            variableName: dataItem.name,
            typeData: dataItem.typeData,
            valueVariable: valueToSend,
            formatVariableKey: /** prefer explicit key when present */ (dataItem.formatVariableKey || (dataItem).format || ''),
            documentId: props?.documentData?.id ?? null,
            typeDocumentId: Number(props?.documentData?.typeDocumentId) || 0
        }

        onCloseEditDialog()
        const fnUpdate = (typeof createOrUpdateVariable === 'function')
            ? createOrUpdateVariable
            : (typeof updateVariable === 'function' ? updateVariable : createVariable)
        await fnUpdate(variableToUpdate)
    }


    /**
     * @param {import('@progress/kendo-react-grid').GridHeaderCellProps} props
     */
    const headerColumnOne = (props) => {
        return <span className='documentPanelBarVariables-header-cell-one'>{props.title}</span>;
    };

    /**
     * @param {import('@progress/kendo-react-grid').GridHeaderCellProps} props
     */
    const headerColumnTwo = (props) => {
        return <span className='documentPanelBarVariables-header-cell-two'>{props.title}</span>;
    };

    /**
     *
     * @param {import('@progress/kendo-react-grid').GridCellProps} props
     */
    const cellColumnOne = (props) => {
        /** @type {{dataItem: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse}} */
        let { dataItem } = props
        const nameVar = (dataItem).variableName ?? dataItem.name
        return (
            <td className='documentPanelBarVariables-cell-one'>
                <span className="gridHLPure-userName">{dataItem.convertedValue || (dataItem?.value?.checked ? dataItem?.value?.true : dataItem?.value?.false)}</span>
                <div className='gridHLPure-detailsContainer'>
                    <span className='gridHLPure-fieldSubtitle'>{nameVar}</span>
                </div>
            </td>
        );
    };

    /**
     * Celda personalizada para las acciones "Ver detalle" y "Eliminar"
     * @param {import('@progress/kendo-react-grid').GridCellProps} props
     */
    const cellColumnActions = (props) => {
        /** @type {{dataItem: import('../../../types/DocumentVariable.types').DocumentVariableGETResponse}} */
        let { dataItem } = props

        if (isInDeleteFlow) return null

        const showTrashIcon = !!(dataItem.value || dataItem.valueVariable)

        const handleViewDetails = () => {
            const name = dataItem.variableName ?? dataItem.name
            const rawValue = dataItem.valueVariable ?? dataItem.value
            const formatVariableKey = dataItem.formatVariableKey ?? dataItem.format
            const formattedValue = dataItem.typeData === 'number'
                ? formatTextUtil.formatVariableValue(rawValue ?? '', formatVariableKey || '')
                : rawValue
            const variableId = dataItem.variableId ?? dataItem.id

            const editableVariable = JSON.parse(JSON.stringify({
                id: variableId,
                variableId,
                name,
                value: formattedValue,
                typeData: dataItem.typeData,
                formatVariableKey,
                entityType: dataItem.entityType
            }))

            setSelectedVariable(editableVariable)
            setGridOptions(prev => ({
                ...prev,
                dataItem: editableVariable,
                showDetails: true
            }))
            setUseEntityValueEdit(false)
            setSelectedEntityTypeEdit('')
            setSelectedEntityValueEdit('')
            setFieldErrors([])
        }

        const handleDelete = () => {
            if (skipDeleteConfirmation) {
                onClickDeleteDirect(dataItem)
            } else {
                onClickDeleteDialog(dataItem)
            }
        }

        return (
            <td className='documentPanelBarVariables-cell-two'>
                <div className={'cellActions-containerBtnsDetails'} style={{ display: 'flex' }}>
                    <div
                        className='cellActions-mobileExpanded'
                        onClick={handleViewDetails}
                    >
                        <img
                            className='cellActions-icon-arrow'
                            alt={'cellActions-arrow'}
                            src={ArrowBtnMobile}
                        />
                    </div>
                    <button
                        className='cellActions-btn'
                        title={t('documentPanelBarVariables.detail.view')}
                        onClick={handleViewDetails}
                    >
                        <p className='cellActions-textBotton'>{t('documentPanelBarVariables.detail.view')}</p>
                    </button>
                    {showTrashIcon && (
                        <button
                            className='cellActions-TrashBtn'
                            onClick={handleDelete}
                            data-id={'labelTrashActions'}
                        >
                            <i
                                title={t('documentPanelBarVariables.delete.title')}
                                className='k-icon k-i-delete k-i-trash'
                            ></i>
                        </button>
                    )}
                </div>
            </td>
        );
    };
    return (
        <div className='documentPanelBarVariables-container'>
            <PanelBarGLPure
                expandedPanel={true}
                titlePanel={t("documentPanelBarVariables.data.custom.title")}
                className='documentPanelBarVariables'
                id='documentPanelBarVariables'
                actionComponent={<ButtonAddHLPure
                    onClickBtn={toggleCreationDialog}
                    customIcon=''
                    disabled={isInDeleteFlow}
                    iconAdd
                    nameBtn={is1040 ?
                        is960
                            ? is530
                                ? ''
                                : t('digitalVaultUploadView.button.add.data')
                            : ''
                        : t('digitalVaultUploadView.button.add.data')}
                />
                }
            >
                <Grid item className='documentPanelBarVariables-grid-documents'>
                    <GridSelectionHLPure
                        styleGeneral={true}
                        styleGeneralClass={'documentPanelBarVariables-general-grid'}
                        pageable={source === 'uploadView'}
                        skip={0}
                        take={source === 'uploadView' ? 4 : variables.length}
                        dataGrid={variables.map(item => ({
                            ...item,
                            iconTrashActive: !!(item.value || item.valueVariable)
                        }))}
                        noRecordsMessage={t('digitalVaultUploadView.errors.no.records.personalizedData') || 'No se encontraron resultados'}
                        filter={true}
                        columnOne={true}
                        columnTwo={isInDeleteFlow ? false : true}
                        onSelectedItem={() => { }}
                        titleRow1={t('documentPanelBarVariables.data.custom')}
                        titleRow2={t('documentPanelBarExpediente-action')}
                        headerColumnOne={headerColumnOne}
                        headerColumnTwo={headerColumnTwo}
                        cellColumnOne={cellColumnOne}
                        cellColumnTwo={cellColumnActions}
                        showDialogYesNoTrash={dialogStates.showDeleteDialog}
                        // Keep actions column fixed to prevent shrinking
                        widthColumnTwo={is860 ? 100 : 150}
                        styleGrid={{ height: source === 'uploadView' ? 'auto' : '' }}
                    />
                </Grid>
            </PanelBarGLPure>
            {dialogStates.showCreationDialog && <InteractiveDialog
                title={t("documentPanelBarVariables.new.data")}
                onClose={toggleCreationDialog}
                onSave={onCreate}
                saveBtnTitle={t('button.save')}
                cancelBtnTitle={t('button.cancel')}>
                <InteractiveContainer>
                    <InteractiveItem>
                        <InputHLPure
                            labelInput={t('digitalVaultUploadView.modal.personalitation.name.data')}
                            valueInput={documentVariable?.name}
                            characterLimit={128}
                            inputActive
                            required
                            typeInput='text'
                            handleChangeInput={
                                /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                (e) => setValue('name', e.target.value)}
                            errorInput={fieldErrors.includes('name')}
                            errorInputText={t('registration.validator.nameValidator')}
                        />
                    </InteractiveItem>
                    <InteractiveItem>
                        <DropDownBtnHLPure
                            required
                            tooltipShow
                            valueItem={'key'}
                            nameOption={'name'}
                            optionsValue={documentVariable.typeData}
                            optionsDropDown={dataOptions}
                            onDropDownChange={
                                /** @type {(e: React.ChangeEvent<HTMLSelectElement>) => void} */
                                (e) => setValue('typeData', e.target.value)}
                            labelDropDown={t('administrationTypeDocumentDataProfile.modal.data.type')}
                            disable={isPrefillMode}
                        />
                    </InteractiveItem>
                    {fieldErrors.includes('typeData') && (<LabelErrorPure label={t('documentPanelBarVariables.error.documentType')} />)}
                    {documentVariable.typeData !== 'boolean' &&
                        <InteractiveItem>
                            <DropDownBtnHLPure
                                required
                                tooltipShow
                                valueItem={'key'}
                                nameOption={'format'}
                                optionsValue={documentVariable.formatVariableKey}
                                optionsDropDown={formatCopy}
                                onDropDownChange={
                                    /** @type {(e: React.ChangeEvent<HTMLSelectElement>) => void} */
                                    (e) => setValue('formatVariableKey', e.target.value)}
                                labelDropDown={t('administrationTypeDocumentDataProfile.modal.format')}
                            />
                        </InteractiveItem>
                    }
                    {documentVariable.typeData !== 'boolean' && fieldErrors.includes('formatVariableKey') && (
                        <LabelErrorPure label={t('documentPanelBarVariables.error.format')} />
                    )}
                    <InteractiveItem>
                        {
                            lockPrefilledValue
                                ? (
                                    documentVariable.typeData === 'date'
                                        ? (
                                            <>
                                                <InputHLPure
                                                    labelInput={t("documentPanelBarVariables.entityValue")}
                                                    valueInput={documentVariable.readingValue}
                                                    characterLimit={128}
                                                    inputActive
                                                    typeInput={'text'}
                                                    disabled
                                                    handleChangeInput={() => { }}
                                                />
                                                <DataPickerHLPure
                                                    labelDataPicker={t("administrationTypeDocumentDataProfile.modal.value") + ' *'}
                                                    handleChangeDate={
                                                        /** @type {(e: import('@progress/kendo-react-dateinputs').DatePickerChangeEvent) => void} */
                                                        (e) => {
                                                            setDocumentVariable(prev => ({
                                                                ...prev,
                                                                value: e.target.value
                                                            }))
                                                        }
                                                    }
                                                    valueDate={
                                                        documentVariable.value instanceof Date
                                                            ? documentVariable.value
                                                            : (typeof documentVariable.value === 'string' && DateTime.fromISO(documentVariable.value).isValid
                                                                ? DateTime.fromISO(documentVariable.value).toJSDate()
                                                                : null)
                                                    }
                                                    errorInput={fieldErrors.includes('entityDate')}
                                                    errorDatePicker={t('documentPanelBarVariables.entityValue.required.dateError')}
                                                />
                                            </>
                                        )
                                        : (
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.modal.value")}
                                                valueInput={formatDocumentVariableValue(documentVariable)}
                                                characterLimit={128}
                                                inputActive
                                                typeInput={documentVariable.typeData === 'number' ? 'number' : 'text'}
                                                disabled
                                                handleChangeInput={() => { }}
                                            />
                                        )
                                )
                                : (
                                    documentVariable.typeData === 'boolean'
                                        ?
                                        <>
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.add.true")}
                                                valueInput={documentVariable.values?.true}
                                                characterLimit={128}
                                                inputActive
                                                typeInput='text'
                                                handleChangeInput={
                                                    /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                                    (e) => setDocumentVariable(prev => ({
                                                        ...prev,
                                                        values: { ...prev.values, true: e.target.value }
                                                    }))}
                                                required
                                                errorInput={fieldErrors.includes('valueTrue')}
                                                errorInputText={t('documentPanelBarVariables.error.true')}
                                            />
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.add.false")}
                                                valueInput={documentVariable.values?.false}
                                                characterLimit={128}
                                                inputActive
                                                typeInput='text'
                                                handleChangeInput={
                                                    /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                                    (e) => setDocumentVariable(prev => ({
                                                        ...prev,
                                                        values: { ...prev.values, false: e.target.value }
                                                    }))}
                                                required
                                                errorInput={fieldErrors.includes('valueFalse')}
                                                errorInputText={t('documentPanelBarVariables.error.false')}
                                            />
                                            <InteractiveItem className='documentPanelBarVariables-switch'>
                                                <LabelHLPure text={documentVariable.name} />
                                                <InputSwitchHLPure onChange={
                                                    /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                                    (e) => {
                                                        setDocumentVariable(prev => ({
                                                            ...prev,
                                                            values: { ...prev.values, checked: e.target.value }
                                                        }))
                                                    }}
                                                    checked={documentVariable.values?.checked} value={documentVariable.values?.checked ? documentVariable.values?.true : documentVariable.values?.false} />
                                            </InteractiveItem>
                                        </>
                                        :
                                        documentVariable.typeData === "date"
                                            ?
                                            <DataPickerHLPure
                                                labelDataPicker={t("administrationTypeDocumentDataProfile.modal.value")}
                                                handleChangeDate={
                                                    /** @type {(e: import('@progress/kendo-react-dateinputs').DatePickerChangeEvent) => void} */
                                                    (e) => setValue('value', e.target.value)
                                                }
                                                valueDate={(documentVariable.value instanceof Date || typeof documentVariable.value === 'string')
                                                    ? (documentVariable.value instanceof Date
                                                        ? documentVariable.value
                                                        : (DateTime.fromISO(documentVariable.value).isValid ? DateTime.fromISO(documentVariable.value).toJSDate() : null))
                                                    : null}
                                            />
                                            :
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.modal.value")}
                                                valueInput={documentVariable.value}
                                                characterLimit={documentVariable.typeData !== 'number' ? 128 : undefined}
                                                inputActive
                                                typeInput={'text'}
                                                placeholder={
                                                    documentVariable.typeData === 'number' && (!documentVariable.value || String(documentVariable.value).trim() === '')
                                                        ? getNumericPlaceholder(documentVariable.formatVariableKey)
                                                        : undefined
                                                }
                                                handleChangeInput={
                                                    /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                                    (e) => {
                                                        const raw = e.target.value ?? ''
                                                        const nextVal = documentVariable.typeData === 'number'
                                                            ? (raw === '' ? '' : formatTextUtil.formatVariableValue(raw, documentVariable.formatVariableKey || ''))
                                                            : raw
                                                        setValue('value', nextVal)
                                                    }
                                                }
                                            />
                                )
                        }
                    </InteractiveItem>
                    {!isPrefillMode && (() => {
                        const ENV = process.env
                        const extracted = entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_EXTRACTED
                        const canUseEntities = extracted && Array.isArray(entities) && entities.length > 0
                        if (!canUseEntities || documentVariable.typeData === 'boolean') return null
                        return (
                            <InteractiveItem>
                                <CheckBoxHLPure
                                    idCheckBox={'use-entity-values'}
                                    labelCheckBox={t('documentPanelBarEntities.useEntityCheckbox')}
                                    checkedState={useEntityValue}
                                    onChangeHandler={
                                        /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                        (e) => {
                                            const checked = e.target.checked
                                            setUseEntityValue(checked)
                                            setLockPrefilledValue(false)
                                            if (!checked) {
                                                setSelectedEntityType('')
                                                setSelectedEntityValue('')
                                            }
                                        }}
                                />
                            </InteractiveItem>
                        )
                    })()}
                    {!isPrefillMode && useEntityValue && (
                        <>
                            <InteractiveItem>
                                <DropDownBtnHLPure
                                    required
                                    valueItem={'key'}
                                    nameOption={'name'}
                                    optionsValue={selectedEntityType}
                                    optionsDropDown={getEntityDropdownOptions(entities, documentVariable?.typeData, '', true)}
                                    onDropDownChange={
                                        /** @type {(e: React.ChangeEvent<HTMLSelectElement>) => void} */
                                        (e) => {
                                            const newType = e.target.value
                                            setSelectedEntityType(newType)
                                            setSelectedEntityValue('')
                                            const mapped = mapEntityTypeToDataType(newType)
                                            setDocumentVariable(prev => ({
                                                ...prev,
                                                typeData: mapped,
                                                formatVariableKey: mapped === 'boolean' ? '' : (formatVariables?.find(f => f.groupName === mapped)?.key || prev.formatVariableKey)
                                            }))
                                            setLockPrefilledValue(false)
                                        }}
                                    labelDropDown={t('followUpDocumentEntities.dropdown.label') || 'Tipo de entidad'}
                                    showError={fieldErrors.includes('entityType')}
                                    errorText={t('documentPanelBarEntities.validation.entityType')}
                                />
                            </InteractiveItem>
                            <InteractiveItem>
                                <DropDownBtnHLPure
                                    required
                                    valueItem={'key'}
                                    nameOption={'name'}
                                    optionsValue={selectedEntityValue}
                                    optionsDropDown={getEntityDropdownOptions(entities, documentVariable?.typeData, selectedEntityType, false)}
                                    onDropDownChange={(e) => {
                                        const newVal = e.target.value
                                        setSelectedEntityValue(newVal)
                                        const mapped = mapEntityTypeToDataType(selectedEntityType)
                                        setDocumentVariable(prev => ({
                                            ...prev,
                                            value: mapped === 'date' ? null : newVal,
                                            readingValue: newVal
                                        }))
                                        setLockPrefilledValue(true)
                                    }}
                                    disable={!selectedEntityType}
                                    labelDropDown={t('documentPanelBarVariables.value')}
                                    showError={fieldErrors.includes('entityValue')}
                                    errorText={t('documentPanelBarEntities.validation.entityValue')}
                                />
                            </InteractiveItem>

                        </>
                    )}
                </InteractiveContainer>
            </InteractiveDialog>
            }
            {
                gridOptions.showDetails &&
                <InteractiveDialog
                    title={t("administrationTypeDocumentDataProfile.modal.title.edit")}
                    onClose={onCloseEditDialog}
                    onSave={onSaveEdit}
                    saveBtnTitle={t('button.save')}
                    cancelBtnTitle={t('button.return')}
                >
                    <InteractiveContainer>
                        <InteractiveItem>
                            {(() => {
                                const di = gridOptions?.dataItem
                                if (di?.typeData === 'date') {
                                    const dateObj = (typeof di?.value === 'string')
                                        ? (DateTime.fromISO(di.value).isValid ? DateTime.fromISO(di.value).toJSDate() : null)
                                        : (di?.value instanceof Date ? di.value : null)

                                    if (useEntityValueEdit) {
                                        return (
                                            <>
                                                <InputHLPure
                                                    labelInput={t("documentPanelBarVariables.entityValue")}
                                                    valueInput={di?.readingValue || ''}
                                                    characterLimit={128}
                                                    typeInput={'text'}
                                                    inputActive
                                                    disabled
                                                    required
                                                />
                                                <DataPickerHLPure
                                                    labelDataPicker={di?.name + ' *'}
                                                    handleChangeDate={
                                                        (e) => setGridOptions(prev => ({
                                                            ...prev,
                                                            dataItem: prev.dataItem ? { ...prev.dataItem, value: e.target.value ? e.target.value.toISOString() : '' } : prev.dataItem
                                                        }))
                                                    }
                                                    valueDate={dateObj}
                                                    errorInput={fieldErrors.includes('valueDate')}
                                                    errorDatePicker={t('documentPanelBarVariables.entityValue.required.dateError')}
                                                />
                                            </>
                                        )
                                    }

                                    return (
                                        <>
                                            <DataPickerHLPure
                                                labelDataPicker={di?.name + ' *'}
                                                handleChangeDate={
                                                    (e) => setGridOptions(prev => ({
                                                        ...prev,
                                                        dataItem: prev.dataItem ? { ...prev.dataItem, value: e.target.value ? e.target.value.toISOString() : '' } : prev.dataItem
                                                    }))
                                                }
                                                valueDate={dateObj}
                                                errorInput={fieldErrors.includes('valueDate')}
                                                errorDatePicker={t('documentPanelBarVariables.entityValue.required.dateError')}
                                            />
                                        </>
                                    )
                                }
                                if (di?.typeData === 'boolean') {
                                    const checked = source === 'detailView' ? !!(di?.entityType?.checked) : !!(di?.value?.checked)
                                    return (
                                        <>
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.add.true")}
                                                valueInput={source === 'detailView' ? di?.entityType?.true : di?.value?.true}
                                                characterLimit={128}
                                                inputActive
                                                typeInput='text'
                                                handleChangeInput={
                                                    (e) => setGridOptions(prev => ({
                                                        ...prev,
                                                        dataItem: {
                                                            ...prev.dataItem,
                                                            entityType: {
                                                                ...(prev.dataItem?.entityType || {}),
                                                                true: e.target.value
                                                            },
                                                            values: {
                                                                ...(prev.dataItem?.values || {}),
                                                                true: e.target.value
                                                            },
                                                            value: {
                                                                ...(prev.dataItem?.value || {}),
                                                                true: e.target.value
                                                            }
                                                        }
                                                    }))
                                                }
                                                required
                                                errorInput={fieldErrors.includes('valueTrue')}
                                                errorInputText={t('documentPanelBarVariables.error.true')}
                                            />
                                            <InputHLPure
                                                labelInput={t("administrationTypeDocumentDataProfile.add.false")}
                                                valueInput={source === 'detailView' ? di?.entityType?.false : di?.value?.false}
                                                characterLimit={128}
                                                inputActive
                                                typeInput='text'
                                                handleChangeInput={
                                                    (e) => setGridOptions(prev => ({
                                                        ...prev,
                                                        dataItem: {
                                                            ...prev.dataItem,
                                                            entityType: {
                                                                ...(prev.dataItem?.entityType || {}),
                                                                false: e.target.value
                                                            },
                                                            values: {
                                                                ...(prev.dataItem?.values || {}),
                                                                false: e.target.value
                                                            },
                                                            value: {
                                                                ...(prev.dataItem?.value || {}),
                                                                false: e.target.value
                                                            }
                                                        }
                                                    }))
                                                }
                                                required
                                                errorInput={fieldErrors.includes('valueFalse')}
                                                errorInputText={t('documentPanelBarVariables.error.false')}
                                            />
                                            <InteractiveItem className='documentPanelBarVariables-switch'>
                                                <LabelHLPure text={di?.name} />
                                                <InputSwitchHLPure onChange={
                                                    (e) => setGridOptions(prev => ({
                                                        ...prev,
                                                        dataItem: prev.dataItem
                                                            ? {
                                                                ...prev.dataItem,
                                                                values: { ...(((prev.dataItem).values) || {}), checked: !checked },
                                                                entityType: { ...(((prev.dataItem).entityType) || {}), checked: !checked },
                                                                value: { ...(((prev.dataItem).value) || {}), checked: !checked }
                                                            }
                                                            : prev.dataItem
                                                    }))
                                                } checked={checked} value={checked} />
                                            </InteractiveItem>
                                        </>
                                    )
                                }
                                return (
                                    <>
                                        <InputHLPure
                                            labelInput={di?.name}
                                            valueInput={di?.value}
                                            characterLimit={di.typeData === 'text' ? 128 : undefined}
                                            inputActive
                                            required
                                            typeInput={'text'}
                                            placeholder={
                                                di?.typeData === 'number' && (di?.value === null || di?.value === undefined || String(di?.value).trim() === '')
                                                    ? getNumericPlaceholder((di).formatVariableKey || (di).format || '')
                                                    : undefined
                                            }
                                            errorInput={fieldErrors.includes('value')}
                                            errorInputText={t('documentPanelBarVariables.edit.input.error')}
                                            handleChangeInput={
                                                /** @type {(e: React.ChangeEvent<HTMLInputElement>) => void} */
                                                (e) => {
                                                    const raw = e.target.value ?? ''
                                                    if (di?.typeData === 'number') {
                                                        const currentFormatKey = (di).formatVariableKey || (di).format || ''
                                                        const formatted = raw === ''
                                                            ? ''
                                                            : formatTextUtil.formatVariableValue(raw, currentFormatKey)
                                                        setGridOptions(prev => ({
                                                            ...prev,
                                                            dataItem: prev.dataItem ? { ...prev.dataItem, value: formatted } : prev.dataItem
                                                        }))
                                                    } else {
                                                        setGridOptions(prev => ({
                                                            ...prev,
                                                            dataItem: prev.dataItem ? { ...prev.dataItem, value: raw } : prev.dataItem
                                                        }))
                                                    }
                                                }
                                            }
                                        />
                                    </>
                                )
                            })()}
                        </InteractiveItem>
                        {(() => {
                            const ENV = process.env
                            const extracted = entitiesStatus === ENV.REACT_APP_EVENT_TRANSACTION_STATUS_OCR_EXTRACTED
                            const canUseEntities = extracted && Array.isArray(entities) && entities.length > 0
                            const di = gridOptions?.dataItem
                            // Deshabilitar checkbox si typeData es 'date' o 'boolean' o no hay entidades
                            const disableCheckbox = di?.typeData === 'boolean'
                            if (!canUseEntities || disableCheckbox) return null
                            return (
                                <InteractiveItem>
                                    <CheckBoxHLPure
                                        idCheckBox={'use-entity-values-edit'}
                                        labelCheckBox={t('documentPanelBarEntities.useEntityCheckbox') || 'Usar valor de entidades'}
                                        checkedState={useEntityValueEdit}
                                        onChangeHandler={
                                            /** @param {React.ChangeEvent<HTMLInputElement>} e */
                                            (e) => {
                                                const checked = e.target.checked
                                                setUseEntityValueEdit(checked)
                                                if (checked) {
                                                    setSelectedEntityTypeEdit('')
                                                    setSelectedEntityValueEdit('')
                                                } else {
                                                    setSelectedEntityTypeEdit('')
                                                    setSelectedEntityValueEdit('')
                                                }
                                            }}
                                    />
                                </InteractiveItem>
                            )
                        })()}
                        {useEntityValueEdit && (
                            <>
                                <InteractiveItem>
                                    <DropDownBtnHLPure
                                        valueItem={'key'}
                                        nameOption={'name'}
                                        optionsValue={selectedEntityTypeEdit}
                                        optionsDropDown={getEntityDropdownOptions(entities, gridOptions?.dataItem?.typeData, '', true)}
                                        onDropDownChange={(e) => {
                                            const newType = e.target.value
                                            setSelectedEntityTypeEdit(newType)
                                            setSelectedEntityValueEdit('')
                                        }}
                                        labelDropDown={t('followUpDocumentEntities.dropdown.label') || 'Tipo de Entidad'}
                                        showError={fieldErrors.includes('entityType')}
                                        errorText={t('documentPanelBarEntities.validation.entityType')}
                                    />
                                </InteractiveItem>
                                <InteractiveItem>
                                    <DropDownBtnHLPure
                                        valueItem={'key'}
                                        nameOption={'name'}
                                        optionsValue={selectedEntityValueEdit}
                                        optionsDropDown={getEntityDropdownOptions(entities, gridOptions?.dataItem?.typeData, selectedEntityTypeEdit, false)}
                                        disable={!selectedEntityTypeEdit}
                                        onDropDownChange={(e) => {

                                            const newVal = e.target.value
                                            setSelectedEntityValueEdit(newVal)
                                            const mapped = mapEntityTypeToDataType(selectedEntityTypeEdit)
                                            let parsedVal = newVal
                                            if (mapped === 'number') {
                                                const fk = (gridOptions?.dataItem)?.formatVariableKey || (gridOptions?.dataItem)?.format || ''
                                                parsedVal = formatTextUtil.formatVariableValue(newVal, fk)
                                            }
                                            setGridOptions(prev => ({
                                                ...prev,
                                                dataItem: prev.dataItem ? { ...prev.dataItem, value: parsedVal, readingValue: newVal } : prev.dataItem
                                            }))
                                        }}
                                        labelDropDown={t('documentPanelBarVariables.value')}
                                        showError={fieldErrors.includes('entityValue')}
                                        errorText={t('documentPanelBarEntities.validation.entityValue')}
                                    />
                                </InteractiveItem>
                            </>
                        )}
                    </InteractiveContainer>
                </InteractiveDialog>
            }
            <DialogSystemGLPure
                showConfirm={dialogStates.showDeleteDialog}
                confirmTitle={t('documentPanelBarVariables.delete.title')}
                labelCancelConfirmation={t('button.cancel')}
                labelSubmitConfirmation={t('button.delete')}
                confirmMessage={t('documentPanelBarVariables.delete.value')}
                handleConfirmDeleteFlowClose={onCloseDeleteDialog}
                handleConfirmDeleteFlowSubmit={async () => {
                    const maybeClear = (props).clearVariableValue
                    if (selectedVariable && typeof maybeClear === 'function') {
                        await maybeClear(selectedVariable)
                    }
                    onCloseDeleteDialog()
                }}
            />
        </div >
    )
}

export default DocumentPanelBarVariables